import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FolderOpen, Search, FileText, FileSpreadsheet, FileImage, Archive, File } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, EmptyState, Card } from "@/components/common";

const ICONS = { spreadsheet: FileSpreadsheet, drawing: FileImage, archive: Archive, document: FileText, template: FileText };
const CAT_LABEL = { pricing: "Pricing", template: "Template", sop: "SOP", reference: "Reference" };

export default function Files() {
  const { companyParam, companyId } = useApp();
  const [q, setQ] = useState("");
  const { data: files = [], isLoading } = useQuery({
    queryKey: ["files", companyId],
    queryFn: async () => (await api.get("/files", { params: companyParam })).data,
  });
  const filtered = files.filter((f) => f.name.toLowerCase().includes(q.toLowerCase()) || (f.tags || []).some((t) => t.includes(q.toLowerCase())));

  return (
    <div data-testid="files-page">
      <PageHeader title="Knowledge / Files" subtitle="Source Memory — indexed local files, SOPs, templates, pricing sheets and standards that agents pull facts from instead of guessing." />

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="files-search" placeholder="Search files, tags…" className="w-full border border-border pl-9 pr-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
      </div>

      {isLoading ? <div className="text-sm text-ink-muted">Loading…</div> : filtered.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No files found" hint="Indexed source files appear here." />
      ) : (
        <Card>
          <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-border bg-surface-muted text-[10px] uppercase tracking-wider font-semibold text-ink-faint">
            <div className="col-span-5">Name</div><div className="col-span-2">Category</div><div className="col-span-3">Tags</div><div className="col-span-1 text-right">Links</div><div className="col-span-1 text-right">Indexed</div>
          </div>
          <div className="divide-y divide-border">
            {filtered.map((f) => {
              const Icon = ICONS[f.file_type] || File;
              return (
                <div key={f.id} className="grid grid-cols-12 gap-3 items-center px-4 py-3" data-testid={`file-row-${f.id}`}>
                  <div className="col-span-5 flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4 text-brand shrink-0" />
                    <span className="text-sm font-mono text-ink truncate">{f.name}</span>
                  </div>
                  <div className="col-span-2 text-xs text-ink-muted">{CAT_LABEL[f.category] || f.category}</div>
                  <div className="col-span-3 flex flex-wrap gap-1">
                    {(f.tags || []).map((t) => <span key={t} className="bg-surface-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-ink-muted">{t}</span>)}
                  </div>
                  <div className="col-span-1 text-right font-mono text-xs text-ink-faint">{(f.linked_jobs || []).length}</div>
                  <div className="col-span-1 text-right">{f.indexed && <span className="w-2 h-2 bg-st-success inline-block" />}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
